"use client";

import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { iCustomerPaymentMethod } from "@sdk/customer-payment-methods/customer-payment-method-model";
import type { iSupplier } from "@sdk/suppliers/supplier-model";
import type { iSupplierPaymentMethod } from "@sdk/supplier-payment-methods/supplier-payment-method-model";
import { isOrganizationDefaultLedgersComplete } from "@sdk/default-ledger-accounts/default-ledger-accounts-model";
import { selectOrganization } from "@/store/modules/workspace/workspace.selectors";

export type QuickAddOption = {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
};

export const QUICK_ADD_SUPPLIER_NAMES = [
  "Hotel Beds",
  "Rate Hawk",
  "Amadeus",
  "Sabre",
  "Travelport",
] as const;

export const QUICK_ADD_CUSTOMER_PAYMENT_METHOD_IDS = {
  bankTransfer: "bank-transfer",
  stripe: "stripe",
} as const;

export const QUICK_ADD_SUPPLIER_PAYMENT_METHOD_IDS = {
  companyCard: "company-card",
} as const;

export function useWorkspaceQuickAddContext() {
  const organization = useSelector(selectOrganization);

  return useMemo(() => {
    const currency =
      organization?.data?.primaryCurrency?.trim().toUpperCase() || "GBP";
    const defaultLedgerAccounts = organization?.data?.defaultLedgerAccounts;
    const mainBankAccountLedgerId =
      defaultLedgerAccounts?.mainBankAccountLedgerId?.trim() || undefined;
    const hasDefaultLedgers =
      isOrganizationDefaultLedgersComplete(defaultLedgerAccounts);

    return {
      currency,
      mainBankAccountLedgerId,
      hasDefaultLedgers,
    };
  }, [organization]);
}

function normalizeName(value: string) {
  return value.trim().toLowerCase();
}

export function buildSupplierQuickAddOptions(
  existingSuppliers: iSupplier[],
  currency: string
): QuickAddOption[] {
  const existingNames = new Set(
    existingSuppliers.map((supplier) => normalizeName(supplier.name ?? ""))
  );

  return QUICK_ADD_SUPPLIER_NAMES.map((name) => {
    const exists = existingNames.has(normalizeName(name));
    return {
      id: normalizeName(name).replace(/\s+/g, "-"),
      label: name,
      description: `Default currency: ${currency}`,
      disabled: exists,
      disabledReason: exists ? "Already added" : undefined,
    };
  });
}

export function buildSupplierQuickAddPayload(name: string, currency: string) {
  return {
    name,
    defaultCurrency: currency,
    productTypes: [] as string[],
  };
}

export function buildCustomerPaymentMethodQuickAddOptions(
  existingPaymentMethods: iCustomerPaymentMethod[],
  context: ReturnType<typeof useWorkspaceQuickAddContext>
): QuickAddOption[] {
  const existingLabels = new Set(
    existingPaymentMethods.map((pm) => normalizeName(pm.label ?? ""))
  );

  const bankTransferExists = existingLabels.has(normalizeName("Bank Transfer"));
  const stripeExists = existingLabels.has(normalizeName("Stripe"));

  return [
    {
      id: QUICK_ADD_CUSTOMER_PAYMENT_METHOD_IDS.bankTransfer,
      label: "Bank Transfer",
      description: context.mainBankAccountLedgerId
        ? `Currency: ${context.currency} · linked to Main Bank Account ledger`
        : `Currency: ${context.currency}`,
      disabled: bankTransferExists || !context.hasDefaultLedgers,
      disabledReason: bankTransferExists
        ? "Already added"
        : !context.hasDefaultLedgers
          ? "Create workspace default ledgers first"
          : undefined,
    },
    {
      id: QUICK_ADD_CUSTOMER_PAYMENT_METHOD_IDS.stripe,
      label: "Stripe",
      description: `Currency: ${context.currency} · PDQ charges at 2%`,
      disabled: stripeExists,
      disabledReason: stripeExists ? "Already added" : undefined,
    },
  ];
}

export function buildCustomerPaymentMethodQuickAddPayload(
  optionId: string,
  context: ReturnType<typeof useWorkspaceQuickAddContext>
) {
  if (optionId === QUICK_ADD_CUSTOMER_PAYMENT_METHOD_IDS.bankTransfer) {
    return {
      label: "Bank Transfer",
      type: "DEFAULT" as const,
      defaultCurrency: context.currency,
      connectedLedger: context.mainBankAccountLedgerId,
      enablePDQCharges: false,
      requirePDQReconcilation: false,
    };
  }

  if (optionId === QUICK_ADD_CUSTOMER_PAYMENT_METHOD_IDS.stripe) {
    return {
      label: "Stripe",
      type: "DEFAULT" as const,
      defaultCurrency: context.currency,
      enablePDQCharges: true,
      PDQChargeFormula: "amount * 0.02",
      requirePDQReconcilation: false,
    };
  }

  return null;
}

export function buildSupplierPaymentMethodQuickAddOptions(
  existingPaymentMethods: iSupplierPaymentMethod[],
  currency: string
): QuickAddOption[] {
  const existingLabels = new Set(
    existingPaymentMethods.map((pm) => normalizeName(pm.label ?? ""))
  );
  const exists = existingLabels.has(normalizeName("Company Card"));

  return [
    {
      id: QUICK_ADD_SUPPLIER_PAYMENT_METHOD_IDS.companyCard,
      label: "Company Card",
      description: `Currency: ${currency}`,
      disabled: exists,
      disabledReason: exists ? "Already added" : undefined,
    },
  ];
}

export function buildSupplierPaymentMethodQuickAddPayload(
  optionId: string,
  currency: string
) {
  if (optionId === QUICK_ADD_SUPPLIER_PAYMENT_METHOD_IDS.companyCard) {
    return {
      label: "Company Card",
      type: "DEFAULT" as const,
      defaultCurrency: currency,
    };
  }

  return null;
}

export function supplierNameForQuickAddOptionId(
  optionId: string
): string | undefined {
  return QUICK_ADD_SUPPLIER_NAMES.find(
    (name) => normalizeName(name).replace(/\s+/g, "-") === optionId
  );
}
